import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  lazy,
  Suspense,
} from "react";
import * as Highcharts from "highcharts/highstock";
import { useTheme } from "../../providers/ThemeProvider";
import { useChartComments } from "../../hooks/useChartComments";
import { useChartCommentPopover } from "./ChartCommentPanel";
import { attachPointContextMenu } from "./pointContextMenu";
import type { ChartComment } from "../../services/ChartCommentService";

const ChartCommentPopover = lazy(() => import("./ChartCommentPanel"));

interface BaseConditionMonitoringChartProps {
  options: Highcharts.Options;
  className?: string;
  style?: React.CSSProperties;
  onChartReady?: (chart: Highcharts.Chart) => void;
  /** Chart comment props — if chartType is set, comment markers + popover are enabled */
  commentProps?: {
    chartType: string;
    machineBK?: string;
  };
}

const BaseConditionMonitoringChart: React.FC<
  BaseConditionMonitoringChartProps
> = ({ options, className = "", style, onChartReady, commentProps }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<Highcharts.Chart | null>(null);
  const { currentTheme } = useTheme();
  const [isZoomed, setIsZoomed] = useState(false);
  const isResettingRef = useRef(false);
  const commentMarkersRef = useRef<any[]>([]);

  const { popoverPoint, openPopover, closePopover } = useChartCommentPopover();
  const { comments: chartComments, refetch: refetchComments } =
    useChartComments({
      chartType: commentProps?.chartType ?? "",
      machineBK: commentProps?.machineBK,
    });

  // Reset zoom function
  const resetZoom = () => {
    if (
      chartInstance.current &&
      chartInstance.current.xAxis &&
      chartInstance.current.xAxis[0]
    ) {
      // Set flag to prevent event handler from interfering
      isResettingRef.current = true;
      setIsZoomed(false);

      chartInstance.current.xAxis[0].setExtremes(undefined, undefined);

      // Reset flag after a delay
      setTimeout(() => {
        isResettingRef.current = false;
      }, 100);
    }
  };

  // Memoize themed options to prevent unnecessary re-creation
  const themedOptions = useMemo(() => {
    // Enhanced xAxis with zoom detection
    const enhancedXAxis = Array.isArray(options.xAxis)
      ? options.xAxis.map((axis) => ({
          ...axis,
          gridLineColor: currentTheme.isDark ? "#374151" : "#e5e7eb",
          lineColor: currentTheme.isDark ? "#6b7280" : "#9ca3af",
          tickColor: currentTheme.isDark ? "#6b7280" : "#9ca3af",
          labels: {
            ...axis.labels,
            style: {
              color: currentTheme.isDark ? "#d1d5db" : "#6b7280",
              fontSize: "12px",
              ...axis.labels?.style,
            },
          },
          events: {
            ...axis.events,
            afterSetExtremes: function (e: any) {
              // Call original event handler if it exists
              if (axis.events?.afterSetExtremes) {
                (axis.events.afterSetExtremes as any).call(this, e);
              }

              // Skip if we're currently resetting
              if (isResettingRef.current) {
                return;
              }

              // Check zoom state when axis extremes change
              setTimeout(() => {
                // Check if chart has data and the current range is different from full range
                if (
                  chartInstance.current &&
                  chartInstance.current.xAxis &&
                  chartInstance.current.xAxis[0]
                ) {
                  const axis = chartInstance.current.xAxis[0];
                  const hasMin = axis.min !== null && axis.min !== undefined;
                  const hasMax = axis.max !== null && axis.max !== undefined;

                  // Get the data range from series
                  let dataMin: number | undefined;
                  let dataMax: number | undefined;

                  if (
                    chartInstance.current.series &&
                    chartInstance.current.series.length > 0
                  ) {
                    const series = chartInstance.current.series[0];
                    if (series.data && series.data.length > 0) {
                      dataMin = series.data[0].x as number;
                      dataMax = series.data[series.data.length - 1].x as number;
                    }
                  }

                  // Consider it zoomed if we have explicit extremes that are different from data range
                  const isCurrentlyZoomed =
                    hasMin &&
                    hasMax &&
                    dataMin !== undefined &&
                    dataMax !== undefined &&
                    axis.min !== null &&
                    axis.max !== null &&
                    (Math.abs((axis.min as number) - dataMin) > 1000 ||
                      Math.abs((axis.max as number) - dataMax) > 1000); // 1 second tolerance

                  setIsZoomed(isCurrentlyZoomed);
                }
              }, 50);
            },
          },
        }))
      : {
          ...options.xAxis,
          gridLineColor: currentTheme.isDark ? "#374151" : "#e5e7eb",
          lineColor: currentTheme.isDark ? "#6b7280" : "#9ca3af",
          tickColor: currentTheme.isDark ? "#6b7280" : "#9ca3af",
          labels: {
            ...options.xAxis?.labels,
            style: {
              color: currentTheme.isDark ? "#d1d5db" : "#6b7280",
              fontSize: "12px",
              ...options.xAxis?.labels?.style,
            },
          },
          events: {
            ...options.xAxis?.events,
            afterSetExtremes: function (e: any) {
              // Call original event handler if it exists
              const xAxisOptions = options.xAxis as any;
              if (xAxisOptions?.events?.afterSetExtremes) {
                xAxisOptions.events.afterSetExtremes.call(this, e);
              }

              // Skip if we're currently resetting
              if (isResettingRef.current) {
                return;
              }

              // Check zoom state when axis extremes change
              setTimeout(() => {
                // Check if chart has data and the current range is different from full range
                if (
                  chartInstance.current &&
                  chartInstance.current.xAxis &&
                  chartInstance.current.xAxis[0]
                ) {
                  const axis = chartInstance.current.xAxis[0];
                  const hasMin = axis.min !== null && axis.min !== undefined;
                  const hasMax = axis.max !== null && axis.max !== undefined;

                  // Get the data range from series
                  let dataMin: number | undefined;
                  let dataMax: number | undefined;

                  if (
                    chartInstance.current.series &&
                    chartInstance.current.series.length > 0
                  ) {
                    const series = chartInstance.current.series[0];
                    if (series.data && series.data.length > 0) {
                      const firstPt = series.data.find((p) => p !== null);
                      const lastPt = [...series.data]
                        .reverse()
                        .find((p) => p !== null);
                      if (firstPt) dataMin = firstPt.x as number;
                      if (lastPt) dataMax = lastPt.x as number;
                    }
                  }

                  // Consider it zoomed if we have explicit extremes that are different from data range
                  const isCurrentlyZoomed =
                    hasMin &&
                    hasMax &&
                    dataMin !== undefined &&
                    dataMax !== undefined &&
                    axis.min !== null &&
                    axis.max !== null &&
                    (Math.abs((axis.min as number) - dataMin) > 1000 ||
                      Math.abs((axis.max as number) - dataMax) > 1000); // 1 second tolerance

                  setIsZoomed(isCurrentlyZoomed);
                }
              }, 50);
            },
          },
        };

    return {
      ...options,
      // Ensure stock chart UI elements are disabled
      rangeSelector: {
        enabled: false,
        ...options.rangeSelector,
      },
      navigator: {
        enabled: false,
        ...options.navigator,
      },
      scrollbar: {
        enabled: false,
        ...options.scrollbar,
      },
      chart: {
        ...options.chart,
        backgroundColor: currentTheme.isDark ? "#1f2937" : "#ffffff",
        style: {
          fontFamily: "Inter, system-ui, sans-serif",
        },
      },
      title: {
        ...options.title,
        style: {
          color: currentTheme.isDark ? "#f9fafb" : "#111827",
          fontSize: "16px",
          fontWeight: "600",
          ...options.title?.style,
        },
      },
      xAxis: enhancedXAxis,
      yAxis: Array.isArray(options.yAxis)
        ? options.yAxis.map((axis) => ({
            ...axis,
            gridLineColor: currentTheme.isDark ? "#374151" : "#e5e7eb",
            lineColor: currentTheme.isDark ? "#6b7280" : "#9ca3af",
            tickColor: currentTheme.isDark ? "#6b7280" : "#9ca3af",
            labels: {
              ...axis.labels,
              style: {
                color: currentTheme.isDark ? "#d1d5db" : "#6b7280",
                fontSize: "12px",
                ...axis.labels?.style,
              },
            },
            title: {
              ...axis.title,
              style: {
                color: currentTheme.isDark ? "#d1d5db" : "#6b7280",
                fontSize: "12px",
                fontWeight: "500",
                ...axis.title?.style,
              },
            },
          }))
        : {
            ...options.yAxis,
            gridLineColor: currentTheme.isDark ? "#374151" : "#e5e7eb",
            lineColor: currentTheme.isDark ? "#6b7280" : "#9ca3af",
            tickColor: currentTheme.isDark ? "#6b7280" : "#9ca3af",
            labels: {
              ...options.yAxis?.labels,
              style: {
                color: currentTheme.isDark ? "#d1d5db" : "#6b7280",
                fontSize: "12px",
                ...options.yAxis?.labels?.style,
              },
            },
            title: {
              ...options.yAxis?.title,
              style: {
                color: currentTheme.isDark ? "#d1d5db" : "#6b7280",
                fontSize: "12px",
                fontWeight: "500",
                ...options.yAxis?.title?.style,
              },
            },
          },
      legend: {
        ...options.legend,
        itemStyle: {
          color: currentTheme.isDark ? "#d1d5db" : "#6b7280",
          fontSize: "12px",
          ...options.legend?.itemStyle,
        },
      },
      tooltip: {
        ...options.tooltip,
        backgroundColor: currentTheme.isDark ? "#374151" : "#ffffff",
        borderColor: currentTheme.isDark ? "#6b7280" : "#d1d5db",
        style: {
          color: currentTheme.isDark ? "#f9fafb" : "#111827",
          fontSize: "12px",
          ...options.tooltip?.style,
        },
      },
    };
  }, [options, currentTheme]);

  // Create chart once on mount
  useEffect(() => {
    if (!chartRef.current) return;

    chartInstance.current = Highcharts.stockChart(
      chartRef.current,
      themedOptions as Highcharts.Options,
    );

    if (onChartReady && chartInstance.current) {
      onChartReady(chartInstance.current);
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps — create once

  // Update chart when options change (data, theme, etc.)
  useEffect(() => {
    if (!chartInstance.current) return;
    chartInstance.current.update(themedOptions as Highcharts.Options, true);
  }, [themedOptions]);

  // ─── Comment markers: draw 💬 icons at timestamps with comments ───
  useEffect(() => {
    const chart = chartInstance.current;
    if (!chart || !commentProps?.chartType) return;

    const drawMarkers = () => {
      commentMarkersRef.current.forEach((m) => {
        try {
          m.destroy();
        } catch {}
      });
      commentMarkersRef.current = [];

      if (!chartComments.length) return;

      const xAxis = chart.xAxis?.[0];
      if (!xAxis) return;
      const extremes = xAxis.getExtremes();

      // Group comments by timestamp (rounded to nearest minute)
      const grouped = new Map<number, ChartComment[]>();
      for (const c of chartComments) {
        const ts = c.timestamp ? new Date(c.timestamp).getTime() : 0;
        if (!ts) continue;
        if (ts < extremes.min || ts > extremes.max) continue;
        const key = Math.round(ts / 60000) * 60000;
        const arr = grouped.get(key) || [];
        arr.push(c);
        grouped.set(key, arr);
      }

      const isDark = currentTheme.isDark;
      grouped.forEach((cmts, tsKey) => {
        const pixelX = xAxis.toPixels(tsKey, false);
        const pixelY = chart.plotTop + 4;
        const count = cmts.length;

        const marker = chart.renderer
          .label(
            count > 1 ? `\uD83D\uDCAC ${count}` : "\uD83D\uDCAC",
            pixelX - 12,
            pixelY,
          )
          .css({ fontSize: "11px", cursor: "pointer" })
          .attr({
            fill: isDark
              ? "rgba(51, 65, 85, 0.9)"
              : "rgba(241, 245, 249, 0.95)",
            stroke: isDark ? "#60a5fa" : "#3b82f6",
            "stroke-width": 1.5,
            r: 6,
            padding: 3,
            zIndex: 8,
          })
          .add();

        marker.on("click", () => {
          const first = cmts[0];
          openPopover({
            x: pixelX,
            y: pixelY + 30,
            category: new Date(tsKey).toISOString(),
            seriesName: "",
            timestamp: first.timestamp,
          });
        });

        commentMarkersRef.current.push(marker);
      });
    };

    drawMarkers();
    Highcharts.addEvent(chart, "redraw", drawMarkers);

    return () => {
      commentMarkersRef.current.forEach((m) => {
        try {
          m.destroy();
        } catch {}
      });
      commentMarkersRef.current = [];
      Highcharts.removeEvent(chart, "redraw", drawMarkers);
    };
  }, [
    chartComments,
    currentTheme.isDark,
    commentProps?.chartType,
    openPopover,
  ]);

  // ─── Point RIGHT-click → open comment popover ───
  // Left-click stays free for zoom/selection; commenting is deliberate.
  useEffect(() => {
    const chart = chartInstance.current;
    if (!chart || !commentProps?.chartType) return;

    return attachPointContextMenu(chart, (point) => {
      const c = point.series.chart;
      const plotX = (point as any).plotX + c.plotLeft;
      const plotY = (point as any).plotY + c.plotTop;
      const ts = point.x; // timestamp ms
      openPopover({
        x: plotX,
        y: plotY,
        category: new Date(ts as number).toISOString(),
        seriesName: point.series.name,
        timestamp: new Date(ts as number).toISOString(),
      });
    });
  }, [commentProps?.chartType, openPopover]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full ${className}`}
      style={style}
    >
      {/* Custom Reset Zoom Button - Only when zoomed */}
      {isZoomed && (
        <button
          onClick={resetZoom}
          className="absolute top-4 right-4 z-20 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded shadow-lg transition-all duration-200 border border-blue-700"
          title="Reset zoom to show all data"
        >
          ↻ Reset Zoom
        </button>
      )}

      <div ref={chartRef} className="w-full h-full" />

      {/* Comment popover — appears on point click */}
      {popoverPoint && commentProps?.chartType && (
        <Suspense fallback={null}>
          <ChartCommentPopover
            chartType={commentProps.chartType}
            machineBK={commentProps.machineBK}
            point={popoverPoint}
            onClose={() => {
              closePopover();
              refetchComments();
            }}
            containerRef={containerRef}
          />
        </Suspense>
      )}
    </div>
  );
};

export default BaseConditionMonitoringChart;
